import { isString, mapKeys } from 'lodash'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import type { EmissionsUnit, EnergyUnit, PowerUnit, TemperatureUnit, TimeUnits } from '../types'
import { isEmissionsUnit, isTemperatureUnit } from '../types/units'

export type UnitFormat = { long: string; short: string }
export type UnitsFormat<T extends string> = Record<T, UnitFormat>

function makeLookup(unitsFormat: UnitsFormat<string>) {
  const lowerCased = mapKeys(unitsFormat, (_, key) => key.toLowerCase())
  return (unit?: string) => {
    if (!isString(unit)) return null
    const lowerUnit = unit.toLowerCase()
    return lowerUnit in lowerCased ? lowerCased[lowerUnit as keyof typeof lowerCased] : null
  }
}

type FormatUnits = <T extends string>(format: UnitsFormat<T>) => (unit?: string) => string
export const formatUnits: FormatUnits = (unitsFormat) => {
  const unitLookup = makeLookup(unitsFormat)
  return (unit) => unitLookup(unit)?.long ?? ''
}

export const formatUnitsAbbr: FormatUnits = (unitsFormat) => {
  const unitLookup = makeLookup(unitsFormat)
  return (unit) => unitLookup(unit)?.short ?? ''
}

export const formatUnitDesc: FormatUnits = (unitsFormat) => (unit) => {
  const long = formatUnits(unitsFormat)(unit)
  const short = formatUnitsAbbr(unitsFormat)(unit)
  return long && short ? `${long} (${short})` : ''
}

type UnitLength = 'long' | 'short'

// Function overloads to provide conditional return types
type UnitsFormatter<T extends string> = {
  (unit: T, length: UnitLength): string
  (unit: undefined, length: UnitLength): undefined
  (unit: string | undefined, length: UnitLength): string | undefined
}

export function useUnitsFormatter() {
  const t = useTranslations('data.units')
  return useMemo(() => {
    return {
      current: (length: UnitLength) => t(`current_${length}`),
      emissions: ((unit: string | undefined, length: UnitLength) => {
        if (isEmissionsUnit(unit)) return t(`emissions_${unit}_${length}`)
      }) as UnitsFormatter<EmissionsUnit>,
      energy: (unit: EnergyUnit, length: UnitLength) => t(`energy_${unit}_${length}`),
      percent: () => t('percent'),
      power: (unit: PowerUnit, length: UnitLength) => t(`power_${unit}_${length}`),
      temperature: ((unit: string | undefined, length: UnitLength) => {
        if (isTemperatureUnit(unit)) return t(`temperature_${unit}_${length}`)
      }) as UnitsFormatter<TemperatureUnit>,
      time: (unit: TimeUnits) => t(`time_${unit}`),
      voltage: (length: UnitLength) => t(`voltage_${length}`)
    }
  }, [t])
}

